import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, Image, Loader2, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { date: "", title: "", classes: "", tag: "", nomination_cutoff: "", nominations_open: true, livegrids_url: "", sanctioning_body: "", poster: "", cancelled: false };

const SANCTIONING_BODIES = ["AASA", "Speedway Australia", "JQMV"];

const POSTER_SLOTS = [
  { key: "main", label: "Main Season Poster", fallback: "/poster-2026-27.jpg", testid: "poster-main" },
  { key: "jqmv", label: "JQMV Poster", fallback: "/jqmv-poster-2026-27.jpg", testid: "poster-jqmv" },
];

const nomClosedFor = (ev) =>
  ev.nominations_open === false || (ev.nomination_cutoff && new Date(ev.nomination_cutoff) < new Date());
const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

export default function AdminFixtures({ token }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [posters, setPosters] = useState({});
  const [admission, setAdmission] = useState(null);
  const [admissionBusy, setAdmissionBusy] = useState(false);
  const [posterBusy, setPosterBusy] = useState("");
  const [evPosterBusy, setEvPosterBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const uploadEventPoster = async (file) => {
    if (!file || !editingId) return;
    setEvPosterBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/events/${editingId}/poster`, fd, { headers });
      setForm((f) => ({ ...f, poster: data.url }));
      setEvents((prev) => prev.map((e) => (e.id === editingId ? { ...e, poster: data.url } : e)));
      toast.success("Meeting poster updated — shows on the Nominations Now Open banner");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setEvPosterBusy(false);
    }
  };

  const loadPosters = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/posters");
      setPosters(data);
    } catch { /* fallbacks stay */ }
  }, []);

  useEffect(() => {
    loadPosters();
    api.get("/settings/admission").then(({ data }) => setAdmission(data)).catch(() => {});
  }, [loadPosters]);

  const saveAdmission = async () => {
    setAdmissionBusy(true);
    try {
      await api.put("/admin/settings/admission", admission, { headers });
      toast.success("Admission prices saved — live on the Race Calendar now");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdmissionBusy(false);
    }
  };

  const uploadPoster = async (slot, file) => {
    if (!file) return;
    setPosterBusy(slot);
    try {
      const fd = new FormData();
      fd.append("slot", slot);
      fd.append("file", file);
      const { data } = await api.post("/admin/settings/poster", fd, { headers });
      setPosters((p) => ({ ...p, [slot]: data.url }));
      toast.success("Poster updated — live on the Race Calendar now");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setPosterBusy("");
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch {
      toast.error("Failed to load calendar");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.date || !form.title.trim()) {
      toast.error("Date and title are required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/events/${editingId}`, form, { headers });
        toast.success("Calendar date updated");
      } else {
        await api.post("/admin/events", form, { headers });
        toast.success("Calendar date added");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (ev) => {
    setForm({
      date: ev.date,
      title: ev.title,
      classes: ev.classes,
      tag: ev.tag,
      nomination_cutoff: ev.nomination_cutoff || "",
      nominations_open: ev.nominations_open !== false,
      livegrids_url: ev.livegrids_url || "",
      sanctioning_body: ev.sanctioning_body || "",
      poster: ev.poster || "",
      cancelled: ev.cancelled === true,
    });
    setEditingId(ev.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this calendar date?")) return;
    try {
      await api.delete(`/admin/events/${id}`, { headers });
      toast.success("Calendar date deleted");
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleNoms = async (ev) => {
    const nextOpen = ev.nominations_open === false;
    try {
      await api.put(`/admin/events/${ev.id}/nominations`, { open: nextOpen }, { headers });
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, nominations_open: nextOpen } : e)));
      toast.success(nextOpen ? `Nominations OPEN — ${ev.title}` : `Nominations CLOSED — ${ev.title}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleCancel = async (ev) => {
    const next = !ev.cancelled;
    if (next && !window.confirm(`Cancel "${ev.title}"? This emails EVERY nominated driver that the meeting is cancelled.`)) return;
    try {
      const { data } = await api.post(`/admin/events/${ev.id}/cancel`, { cancelled: next }, { headers });
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, cancelled: next } : e)));
      toast.success(
        next
          ? `Meeting cancelled — ${data.emailed} driver email${data.emailed === 1 ? "" : "s"} sent${data.email_failures ? ` (${data.email_failures} failed)` : ""}`
          : "Meeting reinstated"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setAllNoms = async (open) => {
    if (!window.confirm(open ? "Reopen nominations for ALL meetings?" : "Close nominations for ALL meetings?")) return;
    try {
      await api.post("/admin/events/nominations-bulk", { open }, { headers });
      toast.success(open ? "Nominations reopened for every meeting" : "Nominations closed for every meeting");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-1">Gate Admission Prices</h2>
        <p className="text-clay-muted text-sm mb-5">Shown at the top of the public Race Calendar. Applies to every meeting.</p>
        {admission && (
          <div className="border border-clay-border bg-clay-card p-5">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[
                ["general", "General Admission $", "admission-general-input"],
                ["member_concession", "Member / Concession $", "admission-concession-input"],
                ["kids_under_15", "Kids Under 15 $", "admission-under-15-input"],
                ["kids_under_10", "Kids Under 10 $ (0 = free)", "admission-under-10-input"],
                ["family", "Family (2+2) $", "admission-family-input"],
              ].map(([key, label, testid]) => (
                <label key={key} className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block">
                  {label}
                  <input
                    data-testid={testid}
                    type="number"
                    min="0"
                    step="0.50"
                    value={admission[key]}
                    onChange={(e) => setAdmission((a) => ({ ...a, [key]: parseFloat(e.target.value) || 0 }))}
                    className={`${inputCls} mt-1.5`}
                  />
                </label>
              ))}
            </div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mt-4">
              Note shown under prices
              <input
                data-testid="admission-note-input"
                type="text"
                value={admission.note}
                onChange={(e) => setAdmission((a) => ({ ...a, note: e.target.value }))}
                className={`${inputCls} mt-1.5 max-w-md`}
              />
            </label>
            <button
              data-testid="admission-save-button"
              onClick={saveAdmission}
              disabled={admissionBusy}
              className={`mt-4 bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-sm px-8 py-3 transition-colors ${admissionBusy ? "opacity-50" : ""}`}
            >
              {admissionBusy ? "Saving…" : "Save Prices"}
            </button>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">Calendar Posters</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {POSTER_SLOTS.map((s) => (
            <div key={s.key} data-testid={`${s.testid}-slot`} className="border border-clay-border bg-clay-card p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay-muted mb-3">{s.label}</p>
              <div className="border border-clay-border/60 overflow-hidden mb-4 max-h-72">
                <img src={posters[s.key] || s.fallback} alt={s.label} className="w-full object-cover" />
              </div>
              <label
                className={`flex items-center justify-center gap-2 border border-dashed border-clay-border hover:border-clay-orange px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] cursor-pointer transition-colors ${posterBusy === s.key ? "opacity-50 pointer-events-none" : ""}`}
              >
                {posterBusy === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                {posterBusy === s.key ? "Uploading…" : "Replace Image"}
                <input
                  data-testid={`${s.testid}-input`}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadPoster(s.key, e.target.files?.[0])}
                />
              </label>
            </div>
          ))}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted mt-4">
          Posters update on the public Race Calendar instantly — no re-publish needed
        </p>
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          {editingId ? "Edit Calendar Date" : "Add Calendar Date"}
        </h2>
        <form data-testid="fixture-form" onSubmit={save} className="border border-clay-border bg-clay-card p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="fixture-date-input" type="date" value={form.date} onChange={set("date")} className={inputCls} required />
          <input data-testid="fixture-title-input" placeholder="Event title * — e.g. Golden Helmet" value={form.title} onChange={set("title")} className={inputCls} required />
          <input data-testid="fixture-classes-input" placeholder="Classes racing — e.g. F500s · Wingless Sprints" value={form.classes} onChange={set("classes")} className={inputCls} />
          <input data-testid="fixture-tag-input" placeholder="Tag — e.g. VIC Title, Signature, AASA Sunday" value={form.tag} onChange={set("tag")} className={inputCls} />
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mb-1.5">
              Sanctioning body (optional — logo goes on the program cover)
            </label>
            <select data-testid="fixture-body-select" value={form.sanctioning_body} onChange={set("sanctioning_body")} className={inputCls}>
              <option value="">None</option>
              {SANCTIONING_BODIES.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mb-1.5">
              Nominations close (optional — leave blank for no cutoff)
            </label>
            <input data-testid="fixture-cutoff-input" type="datetime-local" value={form.nomination_cutoff} onChange={set("nomination_cutoff")} className={inputCls} />
          </div>
          <label
            data-testid="fixture-noms-open-toggle"
            className="flex items-center gap-3 border border-clay-border bg-clay-bg px-4 py-3 cursor-pointer hover:border-clay-orange/60 transition-colors"
          >
            <input
              type="checkbox"
              checked={form.nominations_open}
              onChange={(e) => setForm((f) => ({ ...f, nominations_open: e.target.checked }))}
              className="w-4 h-4 accent-[#E65100]"
            />
            <span className="text-sm">
              Nominations open
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-0.5">
                Untick to close entries now; tick again to reopen or extend past the cutoff
              </span>
            </span>
          </label>
          <div className="sm:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mb-1.5">
              LiveGrids URL (optional — link to this meeting's page on livegrids.com.au)
            </label>
            <input
              data-testid="fixture-livegrids-input"
              type="url"
              placeholder="https://livegrids.com.au/..."
              value={form.livegrids_url}
              onChange={set("livegrids_url")}
              className={inputCls}
            />
          </div>
          {editingId && (
            <div className="sm:col-span-2 border border-clay-border bg-clay-bg p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mb-3">
                Meeting poster (optional — shows on the Nominations Now Open banner at the top of the site)
              </p>
              <div className="flex items-center gap-4">
                {form.poster && (
                  <img src={form.poster} alt="Meeting poster" data-testid="fixture-poster-preview" className="h-24 border border-clay-border object-cover" />
                )}
                <label
                  className={`flex items-center justify-center gap-2 border border-dashed border-clay-border hover:border-clay-orange px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] cursor-pointer transition-colors ${evPosterBusy ? "opacity-50 pointer-events-none" : ""}`}
                >
                  {evPosterBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                  {evPosterBusy ? "Uploading…" : form.poster ? "Replace Poster" : "Upload Poster"}
                  <input
                    data-testid="fixture-poster-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadEventPoster(e.target.files?.[0])}
                  />
                </label>
              </div>
            </div>
          )}
          <div className="sm:col-span-2 flex gap-3">
            {editingId && (
              <button
                data-testid="fixture-cancel-button"
                type="button"
                onClick={() => { setForm(EMPTY); setEditingId(null); }}
                className="flex-1 border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" /> Cancel
              </button>
            )}
            <button
              data-testid="fixture-save-button"
              disabled={saving}
              className="flex-[2] bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors duration-300 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CalendarPlus className="w-5 h-5" />}
              {editingId ? "Save Changes" : "Add Date"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight">
            2026/27 Calendar <span className="text-clay-muted text-xl">({events.length})</span>
          </h2>
          <div className="flex gap-2">
            <button
              data-testid="fixture-open-all-button"
              onClick={() => setAllNoms(true)}
              className="border border-clay-border hover:border-green-400/60 text-clay-muted hover:text-green-300 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 transition-colors"
            >
              Open All Noms
            </button>
            <button
              data-testid="fixture-close-all-button"
              onClick={() => setAllNoms(false)}
              className="border border-clay-border hover:border-red-400/60 text-clay-muted hover:text-red-300 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 transition-colors"
            >
              Close All Noms
            </button>
          </div>
        </div>
        <div className="border border-clay-border">
          {events.length === 0 && (
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted py-12 text-center">No dates yet</p>
          )}
          {events.map((ev) => (
            <div
              key={ev.id}
              data-testid="fixture-row"
              className="grid grid-cols-[auto_1fr_auto] items-center gap-4 sm:gap-6 border-b border-clay-border/60 last:border-b-0 px-4 sm:px-6 py-4 hover:bg-clay-card/60 transition-colors"
            >
              <div className="text-center w-16">
                <div className="font-display font-black text-2xl text-clay-orange leading-none">{ev.date?.slice(8, 10)}</div>
                <div className="font-mono text-[9px] tracking-[0.2em] text-clay-muted mt-1 uppercase">
                  {new Date(`${ev.date}T00:00:00`).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}
                </div>
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold uppercase tracking-tight truncate">{ev.title}</p>
                <p className="text-clay-muted text-xs truncate">{ev.classes}</p>
                {ev.tag && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-gold mt-1">{ev.tag}</p>}
                {ev.cancelled && (
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400 mt-1" data-testid={`fixture-cancelled-badge-${ev.id}`}>
                    Cancelled — nominated drivers emailed
                  </p>
                )}
                {nomClosedFor(ev) ? (
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/80 mt-1">Nominations closed</p>
                ) : ev.nomination_cutoff ? (
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mt-1">
                    Noms close {new Date(ev.nomination_cutoff).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </p>
                ) : (
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-green-400/70 mt-1">Nominations open</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`fixture-cancel-toggle-${ev.id}`}
                  onClick={() => toggleCancel(ev)}
                  className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                    ev.cancelled
                      ? "border-clay-border text-clay-muted hover:border-clay-gold/60 hover:text-clay-gold"
                      : "border-red-500/50 text-red-400 hover:bg-red-500/15"
                  }`}
                >
                  {ev.cancelled ? "Reinstate" : "Cancel Meeting"}
                </button>
                <button
                  data-testid={`fixture-noms-toggle-${ev.id}`}
                  onClick={() => toggleNoms(ev)}
                  className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${
                    ev.nominations_open === false
                      ? "border-red-400/40 text-red-400/80 hover:border-green-400/60 hover:text-green-300"
                      : "border-green-400/40 text-green-400/80 hover:border-red-400/60 hover:text-red-300"
                  }`}
                >
                  {ev.nominations_open === false ? "Noms Closed" : "Noms Open"}
                </button>
                <button data-testid="fixture-edit-button" onClick={() => edit(ev)} className="text-clay-muted hover:text-clay-gold transition-colors p-2" aria-label="Edit fixture">
                  <Pencil className="w-4 h-4" />
                </button>
                <button data-testid="fixture-delete-button" onClick={() => remove(ev.id)} className="text-clay-muted hover:text-red-400 transition-colors p-2" aria-label="Delete fixture">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted mt-4">
          Changes go live on the site instantly — the hero countdown follows the next upcoming date.
        </p>
      </section>
    </div>
  );
}
